import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, BookOpen, ShieldCheck, Briefcase } from "lucide-react";
import {
  SCHOOL_CLASS_STANDARDS,
  classKey,
  getDivisionsForStandard,
  authenticateAccount,
  createStudentAccount,
  createTeacherAccount,
  createAdminAccount,
  createManagementAccount,
  saveSessionUser,
} from "@/lib/eisStore";
import {
  addStudent,
  getTeacherProfileById,
  isSupabaseConfigured,
  saveTeacherSessionFromSupabase,
  saveStudentSessionFromSupabase,
  signInStudent,
  signInTeacher,
  signUpTeacher,
  signUpStudent,
  signOutCurrentUser,
  getStudentProfileById,
} from "@/lib/supabaseOps";

const ROLES = [
  { key: "student", label: "Student / Parent", Icon: GraduationCap },
  { key: "teacher", label: "Teacher", Icon: BookOpen },
  { key: "admin", label: "Admin", Icon: ShieldCheck },
  { key: "management", label: "Management", Icon: Briefcase },
];

export default function Auth() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminName, setAdminName] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [teacherStandard, setTeacherStandard] = useState("");
  const [teacherDivision, setTeacherDivision] = useState("");
  const [studentName, setStudentName] = useState("");
  const [standard, setStandard] = useState("");
  const [division, setDivision] = useState("");
  const studentDivisions = getDivisionsForStandard(standard);
  const teacherDivisions = getDivisionsForStandard(teacherStandard);

  useEffect(() => {
    setMode(params.get("mode") === "signup" ? "signup" : "login");
    setStep(1);
  }, [params]);

  const resetToMode = (nextMode) => {
    setParams({ mode: nextMode });
    setStep(1);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in email and password");
      return;
    }
    if ((role === "admin" || role === "management") && !adminName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (mode === "login") {
      if (role === "teacher" && isSupabaseConfigured) {
        try {
          const authData = await signInTeacher({ email: email.trim(), password });
          const profile = await getTeacherProfileById(authData.user.id);
          const account = await saveTeacherSessionFromSupabase({
            authUser: authData.user,
            profile,
          });
          if (!account) {
            throw new Error("Teacher session could not be created");
          }
          toast.success(`Welcome back, ${account.name || account.email}`);
          navigate("/dashboard");
          return;
        } catch (error) {
          console.error(error);
        }
      }

      if (role === "student" && isSupabaseConfigured) {
        try {
          const authData = await signInStudent({ email: email.trim(), password });
          const profile = await getStudentProfileById(authData.user.id);
          if (!profile) {
            await signOutCurrentUser();
            toast.error("Profile not found. Please contact your teacher.");
            return;
          }
          const account = await saveStudentSessionFromSupabase({
            authUser: authData.user,
            profile,
          });
          if (!account) {
            throw new Error("Student session could not be created");
          }
          toast.success(`Welcome back, ${account.name || account.email}`);
          navigate("/dashboard");
          return;
        } catch (error) {
          console.error(error);
          toast.error(error?.message || "Student login failed");
          return;
        }
      }

      const account = authenticateAccount({ role, email, password });
      if (!account) {
        toast.error("No matching account found");
        return;
      }
      if ((role === "admin" || role === "management") && adminName.trim() && account.name !== adminName.trim()) {
        toast.error("Name does not match the account");
        return;
      }

      saveSessionUser(account);
      toast.success(`Welcome back, ${account.name || account.email}`);
      navigate("/dashboard");
      return;
    }

    if (role === "admin" || role === "management") {
      if (!adminName.trim()) {
        toast.error("Please enter your name");
        return;
      }
      const account =
        role === "admin"
          ? createAdminAccount({
              name: adminName.trim(),
              email: email.trim(),
              password,
            })
          : createManagementAccount({
              name: adminName.trim(),
              email: email.trim(),
              password,
            });
      saveSessionUser(account);
      toast.success(`${role === "admin" ? "Admin" : "Management"} account created`);
      navigate("/dashboard");
      return;
    }

    if (role === "teacher") {
      setStep(2);
      return;
    }

    setStep(2);
  };

  const handleTeacherSignup = async (e) => {
    e.preventDefault();
    if (!teacherName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (teacherStandard !== "none" && (!teacherStandard || !teacherDivision)) {
      toast.error("Please select the class and division");
      return;
    }

    let supabaseUserId = "";
    if (isSupabaseConfigured) {
      try {
        const authData = await signUpTeacher({
          name: teacherName.trim(),
          email: email.trim(),
          password,
          standard: teacherStandard === "none" ? "" : teacherStandard,
          division: teacherStandard === "none" ? "" : teacherDivision,
        });
        supabaseUserId = authData?.user?.id || "";
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Teacher Supabase signup failed");
        return;
      }
    }

    const account = createTeacherAccount({
      id: supabaseUserId || undefined,
      name: teacherName.trim(),
      email: email.trim(),
      password,
      assignedClassKey:
        teacherStandard === "none" ? "" : classKey(teacherStandard, teacherDivision),
    });

    saveSessionUser({
      ...account,
      supabaseUserId: supabaseUserId || account.supabaseUserId || "",
    });
    toast.success("Teacher account created");
    navigate("/dashboard");
  };

  const handleStudentSignup = async (e) => {
    e.preventDefault();
    if (!studentName.trim() || !standard || !division) {
      toast.error("Please fill in student name, standard, and division");
      return;
    }

    const finishSignup = () => {
      const account = createStudentAccount({
        name: studentName.trim(),
        email: email.trim(),
        password,
        studentName: studentName.trim(),
        standard,
        division,
      });

      if (!account) {
        toast.error("Profile not found, please check details or contact your teacher");
        return;
      }

      toast.success("Account created! Please log in to continue.");
      navigate("/auth?mode=login");
    };

    if (isSupabaseConfigured) {
      try {
        const authData = await signUpStudent({
          name: studentName.trim(),
          email: email.trim(),
          password,
          standard,
          division,
        });
        if (!authData?.user?.id) {
          throw new Error("Signup succeeded but no user was returned");
        }
        toast.success("Account created! Please log in to continue.");
        navigate("/auth?mode=login");
        return;
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Student Supabase signup failed");
        return;
      }
    }

    finishSignup();
  };

  const stepTitle =
    mode === "signup"
      ? step === 1
        ? role === "admin" || role === "management"
          ? `Create your ${role} account`
          : "Create your account"
        : role === "teacher"
          ? "Set up your teacher profile"
          : "Tell us about the student"
      : role === "admin" || role === "management"
        ? `Sign in as ${role}`
        : "Sign in to continue";

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
      <Card
        data-testid="auth-card"
        className="w-full max-w-md shadow-xl border-slate-200"
      >
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <img
              src="/img/logo.jpg"
              alt="EIS"
              className="h-20 w-20 object-cover rounded-xl shadow-md"
            />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ color: "#0b1f7a" }}>
            EUREKA INTERNATIONAL SCHOOL
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">{stepTitle}</p>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <>
              <div className="flex bg-slate-100 rounded-full p-1 mb-6">
                <button
                  type="button"
                  data-testid="mode-login-tab"
                  onClick={() => resetToMode("login")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${
                    mode === "login"
                      ? "bg-white shadow text-[#0b1f7a]"
                      : "text-slate-500"
                  }`}
                >
                  Log In
                </button>
                <button
                  type="button"
                  data-testid="mode-signup-tab"
                  onClick={() => resetToMode("signup")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${
                    mode === "signup"
                      ? "bg-white shadow text-[#0b1f7a]"
                      : "text-slate-500"
                  }`}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleStep1} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="auth-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@school.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                </div>

                {(role === "admin" || role === "management") && (
                  <div>
                    <Label htmlFor="admin-name">Name</Label>
                    <Input
                      id="admin-name"
                      data-testid="admin-name-input"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      placeholder="Admin name"
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label className="mb-2 block">I am a</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLES.map(({ key, label, Icon }) => {
                      const active = role === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`role-${key}-btn`}
                          onClick={() => setRole(key)}
                          className={`flex items-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition ${
                            active
                              ? "border-[#0b1f7a] bg-[#0b1f7a] text-white"
                              : "border-slate-200 bg-white text-slate-700 hover:border-[#0b1f7a]"
                          }`}
                        >
                          <Icon size={18} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-btn"
                  className="w-full h-12 rounded-full text-white font-semibold text-base"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  {mode === "signup" ? "Continue" : "Log In"}
                </Button>
              </form>
            </>
          )}

          {step === 2 && mode === "signup" && role === "teacher" && (
            <form onSubmit={handleTeacherSignup} className="space-y-4">
              <div>
                <Label htmlFor="teacher-name">Your Name</Label>
                <Input
                  id="teacher-name"
                  data-testid="teacher-name-input"
                  value={teacherName}
                  onChange={(e) => setTeacherName(e.target.value)}
                  placeholder="Teacher full name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input value={email} disabled className="mt-1 bg-slate-100" />
              </div>

              <div>
                <Label>Password</Label>
                <Input
                  value={password}
                  disabled
                  type="password"
                  className="mt-1 bg-slate-100"
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Class</Label>
                  <Select
                    value={teacherStandard}
                    onValueChange={(value) => {
                      setTeacherStandard(value);
                      setTeacherDivision("");
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not a class teacher</SelectItem>
                      {SCHOOL_CLASS_STANDARDS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Division</Label>
                  <Select
                    value={teacherDivision}
                    onValueChange={setTeacherDivision}
                    disabled={teacherStandard === "none"}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {teacherDivisions.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 h-12 rounded-full"
                  data-testid="teacher-back-btn"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  data-testid="teacher-submit-btn"
                  className="flex-1 h-12 rounded-full text-white font-semibold"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Create Account
                </Button>
              </div>
            </form>
          )}

          {step === 2 && mode === "signup" && role !== "teacher" && (
            <form onSubmit={handleStudentSignup} className="space-y-4">
              <div>
                <Label htmlFor="student-name">Student Name</Label>
                <Input
                  id="student-name"
                  data-testid="signup-student-name"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Full name of student"
                  className="mt-1"
                />
              </div>

              <p className="text-xs text-slate-500">
                This name, standard, and division must match the student record created by your teacher.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Standard</Label>
                  <Select value={standard} onValueChange={setStandard}>
                    <SelectTrigger className="mt-1" data-testid="signup-standard">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOOL_CLASS_STANDARDS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`std-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Division</Label>
                  <Select value={division} onValueChange={setDivision} disabled={!standard}>
                    <SelectTrigger className="mt-1" data-testid="signup-division">
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {studentDivisions.map((item) => (
                        <SelectItem key={item.value} value={item.value} data-testid={`div-option-${item.value}`}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 h-12 rounded-full"
                  data-testid="student-details-back-btn"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  data-testid="student-details-submit-btn"
                  className="flex-1 h-12 rounded-full text-white font-semibold"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Create Account
                </Button>
              </div>
            </form>
          )}

          <p className="text-center text-sm text-slate-500 mt-4">
            <button
              type="button"
              data-testid="back-to-home"
              onClick={() => navigate("/")}
              className="hover:underline"
            >
              {"<"} Back to home
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
